/* =====================================================================
   tower/driver/src/vm-shell.js — view-models for the shell: the top strip, the sync capsule, the offline
   banner, the language toggle and the sync detail screen (driver handheld P1 / 07-ACCEPTANCE B1–B3, C2–C3).

   A view-model is DATA: words already in the driver's language, numbers already formatted, actions named.
   It holds no markup and knows nothing about layouts. Every layout renders the same view-model, so a layout
   can rearrange the screen but can never show a different state, drop an action or claim something the
   model does not say. (The rule for every screen of the handheld: data + actions here, arrangement in
   layouts.js, words in tower/i18n.)

   Honesty: the capsule reads driversync's sync-state model (drvSyncState). "Live" is earned by a confirmed
   server round-trip and never shown while anything waits. The design build has no server, so the capsule
   says "Demo — not connected" rather than inventing a state.
   ===================================================================== */
import { t, fmtTimeL, locale, offeredLocales } from './i18n.js';

const _tm = (x) => (x ? fmtTimeL(new Date(x)) : '');
export function syncState() { try { return typeof window.drvSyncState === 'function' ? window.drvSyncState() : null; } catch (e) { return null; } }

/** The capsule: one honest state, in colour AND word AND icon — a short word on top, the time (or a count)
    under it, so it fits beside the route on a 390px phone in either language. null when nobody is signed in. */
export function capsuleVM(st) {
  st = st === undefined ? syncState() : st;
  if (!st) return { state: 'demo', icon: 'dot', text: t('dh.cap.demo'), sub: t('dh.cap.demoSub'), aria: t('dh.cap.demoAria') };
  if (st.state === 'signedout') return null;
  const last = st.lastOkAt ? _tm(st.lastOkAt) : t('sync.never');
  const aria = (x) => t('dh.cap.aria', { state: x, time: last });
  if (st.flushing) return { state: 'syncing', icon: 'sync', text: t('dh.cap.sending'), sub: '', aria: t('dh.cap.sending') };
  if (st.state === 'offline') return { state: 'offline', icon: 'wifiOff', text: t('dh.cap.offline'), sub: st.pending ? t('dh.cap.saved', { n: st.pending }) : '', aria: t('dh.cap.offlineAria', { n: st.pending }) };
  if (st.state === 'attention') {
    const n = st.attention.length, refused = st.attention.filter((a) => a.refused).length;
    const text = !n ? t('dh.cap.lastFailed') : refused ? t('dh.cap.refused', { n: refused }) : t('dh.cap.retrying', { n });
    return { state: 'attention', icon: 'alert', text, sub: st.pending > n ? t('dh.cap.waiting', { n: st.pending - n }) : last, aria: aria(text) };
  }
  if (st.state === 'waiting') {
    const text = st.pending ? t('dh.cap.waiting', { n: st.pending }) : st.realtime !== 'connected' ? t('dh.cap.notLive') : t('dh.cap.checking');
    return { state: 'waiting', icon: 'dot', text, sub: last, aria: aria(text) };
  }
  return { state: 'live', icon: 'dot', text: t('dh.cap.live'), sub: last, aria: aria(t('dh.cap.live')) };
}

/** The amber bar under the strip — only when the office cannot hear this phone. */
export function bannerVM(st) {
  st = st === undefined ? syncState() : st;
  if (st && st.held) return { text: t('sync.heldBanner', { n: st.held }) };   // storage full: louder than offline, until all are saved / sent
  if (!st || st.state !== 'offline') return null;
  return { text: t('dh.offline.banner', { time: _tm(st.offlineSince || Date.now()), n: st.pending }) };
}

/** O1 — the Off-route button under the strip, on every working screen and in every shift state (Brian: "a button at
    all times — even before a driver ever touches his truck inspection"). The same words everywhere; while the driver
    is off route it says since when, so the session is visible on every screen, not only on the no-route one. */
export function offrouteVM() {
  let s = null; try { s = typeof window.drvOffSession === 'function' ? window.drvOffSession() : null; } catch (e) { s = null; }
  return { label: t('dh.or.btn'), hint: s && s.started ? t('dh.or.btnLive', { time: _tm(s.started) }) : t('dh.or.btnHint'), live: !!s, since: s && s.started ? t('b2.off.since', { time: _tm(s.started) }) : '', call: 'drvOffRouteOpen()' };
}

/** The Alerts bell in the header: the unread count, in words for a reader, and the one tap that opens the list. */
export function alertsVM() {
  let i = null; try { i = typeof window.drvAlertsInfo === 'function' ? window.drvAlertsInfo() : null; } catch (e) { i = null; }
  const n = i ? i.unread : 0;
  return { label: n ? t('dh.alerts.bellAria', { n }) : t('dh.alerts.bell'), n, call: 'drvAlertsOpen()' };
}

/** EN | ES — the languages this company offers; null when there is only one (nothing to switch). */
export function langVM() {
  const ls = offeredLocales(); if (ls.length < 2) return null;
  return { label: t('more.language'), options: ls.map((l) => ({ code: l.code, short: l.code.toUpperCase(), name: l.name, on: l.code === locale() })) };
}

const BUCKETS = ['deliveries', 'payments', 'photos', 'skips', 'loads', 'other'];
/** The sync detail screen (C07): last sync, what is waiting by type, what needs attention, Sync now. */
export function syncDetailVM(st) {
  st = st === undefined ? syncState() : st;
  if (!st) return { demo: true, title: t('dh.sync.title'), text: t('sync.noServer'), capsule: capsuleVM(null) };
  const rt = { connected: t('sync.rt.connected'), reconnecting: t('sync.rt.reconnecting'), polling: t('sync.rt.polling'), off: t('sync.rt.off') }[st.realtime] || st.realtime;
  const rows = BUCKETS.map((b) => ({ id: b, label: t('sync.b.' + b), n: st.byType[b] || 0, oldest: (st.oldestByType && st.oldestByType[b]) ? _tm(st.oldestByType[b]) : '' }));
  const attention = st.attention.map((a) => ({
    title: t('sync.b.' + a.kind) + (a.stopName ? ' · ' + a.stopName : ''),
    status: a.kind === 'storage' ? '' : a.refused ? t('dh.sync.refused') : t('dh.sync.backingOff'),
    reason: a.reason || '',
    told: a.reported ? (a.reportedAt ? t('dh.sync.toldAt', { time: _tm(a.reportedAt) }) : t('sync.d.officeTold')) : '',
    retry: !a.refused && a.nextTryAt ? t('sync.d.retryAt', { time: _tm(a.nextTryAt) }) : '',
    keep: a.money ? t('dh.sync.moneyKept') : '',
    at: _tm(a.queuedAt),
  }));
  const res = st.lastSyncNowResult;
  return {
    demo: false, title: t('dh.sync.title'), capsule: capsuleVM(st),
    lastOk: st.lastOkAt ? _tm(st.lastOkAt) : t('sync.never'), lastOkLabel: t('sync.d.lastOk'),
    realtime: rt, realtimeOk: st.realtime === 'connected', realtimeLabel: t('sync.d.realtime'),
    routeAt: st.lastRouteAt ? _tm(st.lastRouteAt) : t('sync.never'), routeLabel: t('sync.d.lastRoute'),
    offlineSpell: st.lastOffline ? t('dh.sync.lastOffline', { from: _tm(st.lastOffline.from), to: _tm(st.lastOffline.to) }) : '',
    waitingLabel: t('sync.d.waitingHd'), countLabel: t('dh.sync.count'), oldestLabel: t('dh.sync.oldest'),
    rows, pending: st.pending, nothingWaiting: st.pending ? '' : t('sync.d.nothingWaiting'),
    attentionLabel: t('dh.sync.attentionN', { n: attention.length }), attention,
    result: res ? t('sync.d.result', { time: _tm(res.at), sent: res.sent, left: res.left }) : '',
    action: { id: 'syncNow', label: st.flushing ? t('sync.syncing') : t('sync.now'), icon: 'sync', call: 'drvSyncNowUI()', disabled: !!st.flushing, primary: true },
  };
}
